export interface ExtintorQRData {
  tipo?: string;
  marca?: string;
  modelo?: string;
  serie?: string;
  anio?: string;
  raw: string;
}

/**
 * Parsea el texto decodificado de un QR de extintor.
 * Soporta múltiples formatos de fabricantes.
 */
export function parseExtintorQR(text: string): ExtintorQRData {
  const trimmed = text.trim();

  // 1. URL con query params (ej: https://fabricante.com/extintor?sn=01163&modelo=CO22KG)
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return parseURL(trimmed);
  }

  // 2. Formato clave-valor separado por ; (ej: MARCA:TodoExtintor;MODELO:CO22KG;SERIE:L2 01163)
  if (trimmed.includes(':') && trimmed.includes(';')) {
    return parseKeyValue(trimmed);
  }

  // 3. Texto plano tipo "CO22KG 2024 L2 01163"
  return parsePlainText(trimmed);
}

function parseURL(url: string): ExtintorQRData {
  try {
    const parsed = new URL(url);
    const params = parsed.searchParams;

    const aliasMap: Record<string, string[]> = {
      serie: ['sn', 'serie', 'serial', 'num_serie', 'numero_serie'],
      modelo: ['modelo', 'model', 'mod'],
      marca: ['marca', 'brand'],
      tipo: ['tipo', 'type'],
      anio: ['anio', 'year', 'ano', 'fabricacion'],
    };

    const result: ExtintorQRData = { raw: url };

    for (const [field, aliases] of Object.entries(aliasMap)) {
      for (const alias of aliases) {
        const val = params.get(alias);
        if (val) {
          (result as any)[field] = val;
          break;
        }
      }
    }

    return result;
  } catch {
    return { raw: url, serie: url };
  }
}

function parseKeyValue(text: string): ExtintorQRData {
  const result: ExtintorQRData = { raw: text };
  const pairs = text.split(';');

  for (const pair of pairs) {
    const [key, ...rest] = pair.split(':');
    const value = rest.join(':').trim();
    if (!key || !value) continue;

    const k = key.trim().toLowerCase();
    if (['marca', 'brand'].includes(k)) result.marca = value;
    else if (['modelo', 'model', 'mod'].includes(k)) result.modelo = value;
    else if (['serie', 'serial', 'sn', 'num_serie'].includes(k)) result.serie = value;
    else if (['tipo', 'type'].includes(k)) result.tipo = value;
    else if (['anio', 'ano', 'year', 'fabricacion'].includes(k)) result.anio = value;
  }

  return result;
}

function parsePlainText(text: string): ExtintorQRData {
  const result: ExtintorQRData = { raw: text };

  // Buscar año (4 dígitos entre 1990 y 2099)
  const yearMatch = text.match(/\b(19\d{2}|20\d{2})\b/);
  if (yearMatch) {
    result.anio = yearMatch[1];
  }

  // Patrón típico: "MODELO AÑO SERIE" ej: "CO22KG 2024 L2 01163"
  const parts = text.split(/\s+/);

  if (parts.length >= 3 && yearMatch) {
    const yearIndex = parts.indexOf(yearMatch[1]);

    // Todo antes del año = modelo/tipo
    if (yearIndex > 0) {
      result.modelo = parts.slice(0, yearIndex).join(' ');
    } else if (yearIndex === 0 && parts.length > 1) {
      // Año al inicio, siguiente es modelo
      result.modelo = parts[1];
    }

    // Todo después del año = serie
    if (yearIndex < parts.length - 1) {
      result.serie = parts.slice(yearIndex + 1).join(' ');
    }
  } else if (parts.length === 2 && yearMatch) {
    // Solo 2 partes con año
    const yearIndex = parts.indexOf(yearMatch[1]);
    if (yearIndex === 0) {
      result.serie = parts[1];
    } else {
      result.modelo = parts[0];
    }
  } else {
    // No se reconoce patrón, devolver como serie
    result.serie = text;
  }

  return result;
}
